import React, { PointerEvent, ReactNode } from "react";

import { RenderElementProps, RenderLeafProps, useSlate } from "slate-react";

import { GrabsonIcon } from "components/layout";

import { CustomTextKey } from "./custom-types";
import styles from "./TextEditor.scss";
import {
  AlignType,
  CustomElementFormat,
  isAlignElement,
  isAlignType,
  isBlockActive,
  isMarkActive,
  toggleBlock,
  toggleMark,
} from "./utils";

interface ButtonProps {
  active?: boolean;
  reversed?: boolean;
  [key: string]: unknown;
}

export const Button = ({
  active,
  reversed,
  dataTestId,
  ...props
}: ButtonProps) => (
  <button
    {...props}
    type="button"
    className={[
      styles.button,
      active ? "" : styles.inactive,
      reversed ? styles.reversed : "",
    ].join(" ")}
    data-test-id={dataTestId}
  />
);

export const Toolbar = ({ children }: { children: ReactNode }) => (
  <div data-test-id="menu" className={styles.menu}>
    {children}
  </div>
);

interface MarkButtonProps {
  format: CustomTextKey;
  icon: string;
}

export const MarkButton = ({ format, icon }: MarkButtonProps) => {
  const editor = useSlate();
  return (
    <Button
      active={isMarkActive(editor, format)}
      onPointerDown={(event: PointerEvent<HTMLButtonElement>) =>
        event.preventDefault()
      }
      onClick={() => toggleMark(editor, format)}
    >
      <GrabsonIcon name={icon} font="editor" size="normal" />
    </Button>
  );
};

interface BlockButtonProps {
  format: CustomElementFormat;
  icon?: string;
  label?: string;
}

export const BlockButton = ({ format, icon, label }: BlockButtonProps) => {
  const editor = useSlate();
  return (
    <Button
      active={isBlockActive(
        editor,
        format,
        isAlignType(format) ? "align" : "type"
      )}
      onPointerDown={(event: PointerEvent<HTMLButtonElement>) =>
        event.preventDefault()
      }
      onClick={() => toggleBlock(editor, format)}
      dataTestId={`block-button-${format}`}
    >
      {icon ? (
        <GrabsonIcon name={icon} font="editor" size="normal" />
      ) : (
        <span>{label}</span>
      )}
    </Button>
  );
};

export const Element = ({
  attributes,
  children,
  element,
}: RenderElementProps) => {
  const style: React.CSSProperties = {};
  if (isAlignElement(element)) {
    style.textAlign = element.align as AlignType;
  }
  switch (element.type) {
    case "block-quote":
      return (
        <blockquote style={style} {...attributes}>
          {children}
        </blockquote>
      );
    case "bulleted-list":
      return (
        <ul style={style} {...attributes}>
          {children}
        </ul>
      );
    case "heading-one":
      return (
        <h1 style={style} {...attributes}>
          {children}
        </h1>
      );
    case "heading-two":
      return (
        <h2 style={style} {...attributes}>
          {children}
        </h2>
      );
    case "list-item":
      return (
        <li style={style} {...attributes}>
          {children}
        </li>
      );
    case "numbered-list":
      return (
        <ol style={style} {...attributes}>
          {children}
        </ol>
      );
    default:
      return (
        <p style={style} {...attributes}>
          {children}
        </p>
      );
  }
};

export const Leaf = ({
  attributes,
  children: childrenProp,
  leaf,
}: RenderLeafProps) => {
  let children = childrenProp;
  if (leaf.bold) {
    children = <strong>{children}</strong>;
  }

  if (leaf.code) {
    children = <code>{children}</code>;
  }

  if (leaf.italic) {
    children = <em>{children}</em>;
  }

  if (leaf.underline) {
    children = <u>{children}</u>;
  }

  return <span {...attributes}>{children}</span>;
};
